import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Spinner, Stack, Checkbox, useBoolean } from '@chakra-ui/react';
import ScriptDBFileItem from './ScriptDBFileItem';
import ConsoleOutput from './ConsoleOutput';
import {
    DatabaseConfigEnv,
    DatabaseDeployTarget,
    RecordDatabaseConnection,
    WebSocketDeploymentResponse,
    SqlContentEntity,
    TransportRequestScriptDB,
    QueryLogResponse
} from 'data/schemas/TransportRequest';

interface RunScriptDBProps {
    scriptDBContents: Array<TransportRequestScriptDB & { error?: Error }>;
    databaseConfigEnvs: DatabaseConfigEnv[];
    isLoading: boolean;
    onClickConnectDatabase: (selectedDatabases: DatabaseDeployTarget[]) => void;
    databasesConnected: RecordDatabaseConnection[];
    onClickRunQuery: (
        isRunTest: boolean,
        scriptDB: SqlContentEntity,
        selectedDatabases: DatabaseDeployTarget[]
    ) => void;
    consoleData: WebSocketDeploymentResponse[];
    handleFetchQueryLogs: () => void;
    queryLogResult: QueryLogResponse[] | null;
}

function RunScriptDB(props: RunScriptDBProps) {
    const {
        scriptDBContents,
        databaseConfigEnvs,
        isLoading,
        onClickConnectDatabase,
        databasesConnected,
        onClickRunQuery,
        consoleData,
        handleFetchQueryLogs,
        queryLogResult
    } = props;
    const isNoScriptDB = !scriptDBContents || scriptDBContents.length === 0;
    const [isShowLogs, setIsShowLogs] = useBoolean(false);
    const [isOpenConsole, setOpenConsole] = useBoolean(false);
    const [selectedDatabases, setSelectedDatabases] = useState<DatabaseDeployTarget[]>([]);
    const [selectedFile, setSelectedFile] = useState<SqlContentEntity>();
    const [consoleOutput, setConsoleOutput] = useState<string>('');

    useEffect(() => {
        // console.log('output', consoleData);
        const scriptDBResults = consoleData
            .map(item => item?.scriptdb_result)
            .filter((item): item is NonNullable<typeof item> => item !== undefined);

        const executeResultMessages = scriptDBResults
            .map(item => {
                if (item?.execute_result) {
                    return item?.message.concat(
                        '\n\texecute_result: ',
                        '\n\t\t- database: ',
                        item.execute_result.db_name,
                        '\n\t\t- status: ',
                        item.execute_result.status,
                        '\n\t\t- message: ',
                        `"${item.execute_result.message}"\r\n`
                    );
                }
                return item?.message;
            })
            .reverse();
        setConsoleOutput(executeResultMessages.join('\n'));
    }, [consoleData]);

    const handleSelectDatabases = useCallback(
        (selectedDatabase: DatabaseDeployTarget) => {
            setSelectedDatabases(prevState => {
                const exists = prevState.some(d => d.id === selectedDatabase.id);
                if (exists) {
                    // If already selected, remove it (handle uncheck)
                    return prevState.filter(d => d.id !== selectedDatabase.id);
                }
                // If not selected, add it
                return [...prevState, selectedDatabase];
            });
        },
        [selectedDatabases]
    );

    const handleSelectedFile = useCallback(
        (fileInfo: SqlContentEntity) => {
            // console.log('[Debug] selected file:', fileInfo);
            // if (fileInfo.Status && fileInfo.Status.toLowerCase() === 'online') {
            //     return;
            // }

            setSelectedFile(fileInfo);
        },
        [selectedFile]
    );

    const handleClickRunQuery = useCallback(
        (isTestQuery: boolean) => {
            if (!selectedFile) {
                return;
            }
            setOpenConsole.on();
            onClickRunQuery(isTestQuery, selectedFile, selectedDatabases);
        },
        [selectedFile, selectedDatabases]
    );

    const handleClickConnect = useCallback(() => {
        handleClickConsole();
        onClickConnectDatabase(selectedDatabases);
    }, [selectedDatabases]);

    const handleClickShowLogs = useCallback(() => {
        if (isShowLogs) {
            setIsShowLogs.off();
            setOpenConsole.off();
        } else {
            setIsShowLogs.on();
            setOpenConsole.off();
            handleFetchQueryLogs();
        }
    }, [isShowLogs]);

    const handleClickConsole = useCallback(() => {
        if (isOpenConsole) {
            setOpenConsole.off();
            setIsShowLogs.off();
        } else {
            setOpenConsole.on();
            setIsShowLogs.off();
        }
    }, [isOpenConsole]);

    const isDatabaseConnected = useCallback(
        (databaseName: string) => {
            if (databasesConnected.length === 0) return false;

            const foundConnectionDatabase = databasesConnected.find(
                database => database.db_name === databaseName
            );

            return foundConnectionDatabase ? true : false;
        },
        [databasesConnected]
    );

    const formatQueryLogs = useCallback(() => {
        if (!queryLogResult || queryLogResult.length === 0) return [];

        // Sort logs by createdOn timestamp ascending when possible
        const sorted = queryLogResult.slice().sort((a, b) => {
            const ta = Date.parse(a.createdOn || '') || 0;
            const tb = Date.parse(b.createdOn || '') || 0;
            return ta - tb;
        });

        const startTimestamp = sorted[0]?.createdOn || '';
        const endTimestamp = sorted[sorted.length - 1]?.createdOn || '';

        const pad = (n: number) => n.toString().padStart(2, '0');
        const formatDate = (iso?: string) => {
            if (!iso) return '';
            const d = new Date(iso);
            if (isNaN(d.getTime())) return iso;
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
                d.getHours()
            )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        };

        const logMessages = queryLogResult.map(log => {
            let formattedMessage = '';
            formattedMessage += `${'='.repeat(80)}\n`;
            formattedMessage += `📄 Query Log for: ${log.ScriptDBName}\n`;
            formattedMessage += `🧾 Entries: ${sorted.length}   ⏰ Time Range: ${formatDate(
                startTimestamp
            )} to ${formatDate(endTimestamp)}\n`;
            formattedMessage += `${'='.repeat(80)}\n\n`;

            const sqlKeywords = [
                'SELECT',
                'INSERT',
                'UPDATE',
                'DELETE',
                'CREATE',
                'DROP',
                'ALTER',
                'TRUNCATE',
                'MERGE',
                'WITH'
            ];

            const entries = sorted.map(entry => {
                const ts = formatDate(entry.createdOn);
                const level = (entry.logLevel || '').toUpperCase();
                let levelLabel = '';
                if (level.includes('ERROR') || level.includes('ERR')) levelLabel = '❌ ERROR';
                else if (level.includes('WARN')) levelLabel = '⚠️ WARN';
                else if (level.includes('INFO')) levelLabel = 'ℹ️ INFO';
                else levelLabel = level ? `🔔 ${entry.logLevel}` : '🔔 LOG';

                // message may be an object with { message, state } or a plain string
                const msgObj: any = entry.message;
                let msgText = '';
                let stateText = '';
                if (!msgObj) {
                    msgText = '';
                } else if (typeof msgObj === 'string') {
                    msgText = msgObj;
                } else if (typeof msgObj.message === 'string') {
                    msgText = msgObj.message;
                    if (msgObj.state) stateText = ` [state: ${msgObj.state}]`;
                } else {
                    try {
                        msgText = JSON.stringify(msgObj, null, 2);
                    } catch (e) {
                        msgText = String(msgObj);
                    }
                }

                const trimmed = msgText.trim();
                const upper = trimmed.toUpperCase();

                // If it looks like SQL, put it on the next indented lines
                if (sqlKeywords.some(k => upper.startsWith(k))) {
                    const indented = trimmed
                        .split('\n')
                        .map(line => `    ${line}`)
                        .join('\n');
                    return `[${ts}] ${levelLabel}${stateText}\n${indented}`;
                }

                // For multi-line messages, indent subsequent lines
                const lines = trimmed.split('\n');
                if (lines.length > 1) {
                    const indented = lines.map((l, i) => (i === 0 ? l : `    ${l}`)).join('\n');
                    return `[${ts}] ${levelLabel}${stateText} ${indented}`;
                }

                return `[${ts}] ${levelLabel}${stateText} ${trimmed}`;
            });

            formattedMessage += entries.join('\n\n');
            return formattedMessage;
        });

        return logMessages;
    }, [queryLogResult]);

    return (
        <div className="flex flex-col gap-2">
            <div className="rounded border px-3 py-2">
                {/*Databases selector and Connect to database*/}
                <div className="flex flex-row">
                    {/* Left column with groups of buttons */}
                    <div className="flex flex-1 gap-4">
                        {/*Group 1*/}
                        <div className="flex flex-col">
                            <div className="flex gap-2">
                                <details className="flex w-full flex-col">
                                    <summary className="cursor-pointer select-none rounded bg-yellow-400 px-2 py-1 text-sm">
                                        Mafaung
                                    </summary>
                                    <Stack
                                        mt={1}
                                        spacing={1}
                                        className="flex max-h-[180px] min-w-[200px] flex-col flex-wrap"
                                    >
                                        {databaseConfigEnvs
                                            .filter(env => env.systemGroup === 'Mafaung')
                                            .map(env => (
                                                <Checkbox
                                                    key={env.id}
                                                    size="sm"
                                                    margin={1}
                                                    colorScheme="green"
                                                    disabled={isDatabaseConnected(env.databaseName)}
                                                    onChange={() =>
                                                        handleSelectDatabases({
                                                            id: env.id,
                                                            key: env.key,
                                                            db_name: env.databaseName,
                                                            version: env.databaseVersion
                                                        })
                                                    }
                                                >
                                                    {env.databaseName}
                                                </Checkbox>
                                            ))}
                                    </Stack>
                                </details>
                            </div>
                        </div>
                        {/*Group 2*/}
                        <div className="flex flex-col gap-2">
                            <div className="flex gap-2">
                                <details className="flex w-full flex-col">
                                    <summary className="cursor-pointer select-none rounded bg-sky-300 px-2 py-1 text-sm">
                                        STD
                                    </summary>
                                    <Stack
                                        mt={1}
                                        spacing={1}
                                        className="flex max-h-[180px] flex-col flex-wrap"
                                    >
                                        {databaseConfigEnvs
                                            .filter(env => env.systemGroup === 'STD')
                                            .map(env => (
                                                <Checkbox
                                                    key={env.id}
                                                    size="sm"
                                                    margin={1}
                                                    colorScheme="green"
                                                    disabled={isDatabaseConnected(env.databaseName)}
                                                    onChange={() =>
                                                        handleSelectDatabases({
                                                            id: env.id,
                                                            key: env.key,
                                                            db_name: env.databaseName,
                                                            version: env.databaseVersion
                                                        })
                                                    }
                                                >
                                                    {env.databaseName}
                                                </Checkbox>
                                            ))}
                                    </Stack>
                                </details>
                            </div>
                        </div>
                    </div>

                    {/* Right column with Connect and Test buttons */}
                    <div className="flex flex-col items-start gap-2">
                        <Button
                            colorScheme="blue"
                            disabled={isNoScriptDB}
                            onClick={() => handleClickConnect()}
                        >
                            Connect
                        </Button>
                    </div>
                </div>
            </div>

            <div className="min-h-80 rounded border px-3 py-2">
                {/*ScriptDB file from UAT Tag*/}
                <div className="flex flex-col gap-2">
                    {/*Operator*/}
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            colorScheme="gray"
                            disabled={isNoScriptDB}
                            onClick={() => handleClickRunQuery(true)}
                        >
                            Test Query
                        </Button>
                        <Button
                            size="sm"
                            className="w-[110px] bg-yellow-400"
                            disabled={isNoScriptDB}
                            onClick={() => handleClickShowLogs()}
                        >
                            {isShowLogs ? 'Show Query' : 'Show Log'}
                        </Button>
                        <Button
                            size="sm"
                            colorScheme="green"
                            onClick={() => handleClickConsole()}
                        >
                            Console
                        </Button>
                        <div className="flex flex-1 items-end justify-end">
                            <Button
                                size="sm"
                                colorScheme="blue"
                                disabled={isNoScriptDB}
                                onClick={() => handleClickRunQuery(false)}
                            >
                                Run Query
                            </Button>
                        </div>
                    </div>

                    {/*File list*/}
                    <div className="flex flex-col gap-2">
                        {isLoading ? (
                            <Spinner />
                        ) : isNoScriptDB ? (
                            <div className="text-center text-lg font-bold text-gray-500">
                                No Content.
                            </div>
                        ) : (
                            <ScriptDBFileItem
                                scriptDBs={scriptDBContents}
                                isShowLogs={isShowLogs}
                                isOpenConsole={isOpenConsole}
                                onSelectedFile={fileInfo => handleSelectedFile(fileInfo)}
                                queryLogs={formatQueryLogs()}
                            />
                        )}
                    </div>

                    {/* Single shared Console Output */}
                    <ConsoleOutput
                        output={consoleOutput}
                        isOpen={isOpenConsole}
                    />
                </div>
            </div>
        </div>
    );
}

export default observer(RunScriptDB);
